import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DollarSign } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function WeeklyTrendsChart({ weeklyData }) {
  const [chartView, setChartView] = useState('leads'); // 'leads' or 'revenue'

  if (!weeklyData || !weeklyData.weeks || weeklyData.weeks.length === 0) {
    return null;
  }

  // Check if there's any revenue data
  const hasRevenue = weeklyData.weeks.some(week => week.revenue > 0);

  return (
    <Card className="card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
            6-Week Trends
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant={chartView === 'leads' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartView('leads')}
              className="text-sm"
            >
              # Leads
            </Button>
            <Button
              variant={chartView === 'revenue' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setChartView('revenue')}
              className="text-sm"
            >
              <DollarSign className="h-4 w-4 mr-1" />
              Revenue
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartView === 'revenue' && !hasRevenue ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="text-6xl mb-4">💰</div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Revenue Data Yet</h3>
            <p className="text-sm text-gray-500 text-center max-w-md">
              Revenue tracking requires leads to be matched with Gingr POS customers. Once leads convert and are matched to POS records, their revenue will appear here.
            </p>
            <p className="text-xs text-gray-400 mt-4">
              Tip: Check the Leads page to see POS match status for converted leads
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            {chartView === 'leads' ? (
              <BarChart data={weeklyData.weeks}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="leads_received" fill="#1F8A7C" name="Received" radius={[3, 3, 0, 0]} />
                <Bar dataKey="leads_actioned" fill="#8B8AE0" name="Actioned" radius={[3, 3, 0, 0]} />
              </BarChart>
            ) : (
              <LineChart data={weeklyData.weeks}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#1F8A7C" strokeWidth={2.5} name="Revenue ($)" />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default WeeklyTrendsChart;
